/**
 * StreamDetail - Mixcloud/SoundCloud Inspired Design
 *
 * Waveform-centric layout with all features:
 * - DJ Set Track Analysis
 * - Comments with replies
 * - Similar Streams (AI)
 * - Dig Deeper (vinyl matches from Tempest Record)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { logger } from '../utils/logger';
import { normalizeGenre, taggedGenre } from '../utils/genreNormalization';
import streamService, { Stream } from '../services/streams';
import socialService, { Comment, StreamFavoriteStats, StreamPlayStats, groupCommentsIntoMarkers, CommentMarker } from '../services/social';
import aiService, { AISearchResult, VinylMatch } from '../services/aiService';
import djSetService, { DJSet, TrackSegment, TrackVinylMatch } from '../services/djSetService';
import showNotesService, { ShowNotes } from '../services/showNotesService';
import { useOffline } from '../context/OfflineContext';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useAuthPrompt } from '../contexts/AuthPromptContext';
import { rewriteAudioUrl } from '../utils/audioUrl';
import DownloadButton from '../components/DownloadButton';
import TrackTimeline, { buildPressingSummary } from '../components/TrackTimeline';
import WaveformViewer from '../components/WaveformViewer';
import WaveformCommentMarkers from '../components/WaveformCommentMarkers';
import LineageHeritage from '../components/LineageHeritage';
import { digDeeperHref } from '../utils/digDeeperLink';
import './StreamDetail.css';

/**
 * One Dig Deeper vinyl row. A link to the page that shows the match
 * (`digDeeperHref`, TEM-1662), or the same row with no link when no page can.
 */
const DigDeeperRow: React.FC<{ matchId: string; children: React.ReactNode }> = ({ matchId, children }) => {
  const href = digDeeperHref(matchId);
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="v2-vinyl-item">{children}</a>
  ) : (
    <div className="v2-vinyl-item">{children}</div>
  );
};

const StreamDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getOfflineUrl } = useOffline();
  const { playStream: playGlobalStream, currentStream, isPlaying: globalIsPlaying, currentTime, duration, seek, addToQueue, queue } = useAudioPlayer();
  const { requireAuth, user: authUser } = useAuthPrompt();

  // Core state
  const [stream, setStream] = useState<Stream | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');

  // Social state
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteStats, setFavoriteStats] = useState<StreamFavoriteStats | null>(null);
  const [playStats, setPlayStats] = useState<StreamPlayStats | null>(null);
  const [hasRecordedPlay, setHasRecordedPlay] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // AI state
  const [similarStreams, setSimilarStreams] = useState<AISearchResult[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);

  // Vinyl matches state (Dig Deeper)
  const [vinylMatches, setVinylMatches] = useState<VinylMatch[]>([]);
  const [loadingVinyl, setLoadingVinyl] = useState(false);
  const [vinylMessage, setVinylMessage] = useState<string | null>(null);

  // DJ Set state
  const [djSet, setDJSet] = useState<DJSet | null>(null);
  const [djSetTracks, setDJSetTracks] = useState<TrackSegment[]>([]);
  const [, setLoadingDJSet] = useState(false);
  const [, setProcessingDJSet] = useState(false);
  const [, setDJSetError] = useState<string | null>(null);

  // Per-track vinyl modal
  const [selectedTrack, setSelectedTrack] = useState<TrackSegment | null>(null);
  const [trackVinylMatches, setTrackVinylMatches] = useState<TrackVinylMatch[]>([]);
  const [loadingTrackVinyl, setLoadingTrackVinyl] = useState(false);
  const [showTrackVinylModal, setShowTrackVinylModal] = useState(false);

  // Show Notes state
  const [showNotes, setShowNotes] = useState<ShowNotes | null>(null);
  const [loadingShowNotes, setLoadingShowNotes] = useState(false);
  const [generatingShowNotes, setGeneratingShowNotes] = useState(false);
  const [showNotesError, setShowNotesError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);

  // Waveform comments state
  const [commentAtTimestamp, setCommentAtTimestamp] = useState(false);
  const [commentFilter, setCommentFilter] = useState<'all' | 'timestamped' | 'general'>('all');
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);

  // Computed
  const isCurrentStream = currentStream?.streamId === stream?.streamId;
  const isPlaying = isCurrentStream && globalIsPlaying;
  const isOwner = !!(authUser?.userId && stream?.createdBy && authUser.userId === stream.createdBy);

  // Comment markers for waveform (only timestamped comments, clustered into markers)
  const commentMarkers = React.useMemo(
    () => groupCommentsIntoMarkers(comments),
    [comments]
  );

  // Filtered comments based on filter selection
  const filteredComments = React.useMemo(() => {
    switch (commentFilter) {
      case 'timestamped':
        return comments.filter(c => c.timestampMs !== undefined && !c.parentId);
      case 'general':
        return comments.filter(c => c.timestampMs === undefined && !c.parentId);
      case 'all':
      default:
        return comments.filter(c => !c.parentId);
    }
  }, [comments, commentFilter]);

  useEffect(() => {
    if (id) {
      loadStream(id);
      loadSocialData(id);
      loadSimilarStreams(id);
      loadVinylMatches(id);
      loadDJSet(id);
      loadShowNotes(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll for DJ Set status
  useEffect(() => {
    if (!id || djSet?.status !== 'processing') return;
    const pollInterval = setInterval(async () => {
      try {
        const response = await djSetService.getDJSetByStream(id);
        if (response.exists && response.set) {
          if (response.set.status === 'ready') {
            setDJSet(response.set);
            setDJSetTracks(response.tracks || []);
          } else if (response.set.status === 'failed') {
            setDJSet(response.set);
            setDJSetError('Track analysis failed. Please try again.');
          }
        }
      } catch (err: any) {
        logger.warn('[StreamDetail] Error polling DJ Set', { error: err?.message });
      }
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [id, djSet?.status]);

  // Poll for Show Notes status
  useEffect(() => {
    if (!id || showNotes?.status !== 'processing') return;
    const pollInterval = setInterval(async () => {
      try {
        const notes = await showNotesService.getShowNotes(id);
        if (notes) {
          setShowNotes(notes);
          if (notes.status === 'failed') {
            setShowNotesError('Show notes generation failed. Please try again.');
          }
        }
      } catch (err: any) {
        logger.warn('[StreamDetail] Error polling show notes', { error: err?.message });
      }
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [id, showNotes?.status]);

  // Record play
  useEffect(() => {
    if (isPlaying && stream && !hasRecordedPlay) {
      socialService.recordPlay(stream.streamId).catch(() => {});
      setHasRecordedPlay(true);
    }
  }, [isPlaying, stream, hasRecordedPlay]);

  const loadStream = async (streamId: string) => {
    try {
      setLoading(true);
      const data = await streamService.getStreamById(streamId);
      setStream(data);
      const offlineUrl = await getOfflineUrl(streamId);
      setAudioUrl(offlineUrl || rewriteAudioUrl(data.audioUrl));
    } catch (err: any) {
      logger.error('[StreamDetail] Error loading stream', err);
      setError(err.message || 'Failed to load stream');
    } finally {
      setLoading(false);
    }
  };

  const loadSocialData = async (streamId: string) => {
    try {
      setLoadingComments(true);
      const [commentsData, favStats, plStats, userFav] = await Promise.all([
        socialService.getComments(streamId),
        socialService.getFavoriteStats(streamId),
        socialService.getPlayStats(streamId),
        socialService.getUserFavorite(streamId).catch(() => null),
      ]);
      setComments(commentsData);
      setFavoriteStats(favStats);
      setPlayStats(plStats);
      setIsFavorited(!!userFav);
    } catch (err: any) {
      logger.error('[StreamDetail] Error loading social data', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const loadSimilarStreams = async (streamId: string) => {
    try {
      setLoadingSimilar(true);
      const results = await aiService.findSimilarStreams(streamId, 5);
      setSimilarStreams(results);
    } catch (err: any) {
      logger.error('[StreamDetail] Error loading similar', err);
      setSimilarStreams([]);
    } finally {
      setLoadingSimilar(false);
    }
  };

  const loadVinylMatches = async (streamId: string) => {
    try {
      setLoadingVinyl(true);
      setVinylMessage(null);
      const response = await aiService.findVinylMatches(streamId);
      setVinylMatches(response.matches);
      if (!response.hasMatches && response.matches.length === 0 && !response.userId) {
        setVinylMessage('Login to see vinyl matches from your Tempest Record collection');
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setVinylMessage('Login to see vinyl matches from your Tempest Record collection');
      }
      setVinylMatches([]);
    } finally {
      setLoadingVinyl(false);
    }
  };

  const loadDJSet = async (streamId: string) => {
    try {
      setLoadingDJSet(true);
      setDJSetError(null);
      const response = await djSetService.getDJSetByStream(streamId);
      if (response.exists && response.set) {
        setDJSet(response.set);
        setDJSetTracks(response.tracks || []);
      }
    } catch (err: any) {
      logger.info('[StreamDetail] DJ Set unavailable', { error: err });
    } finally {
      setLoadingDJSet(false);
    }
  };

  const loadShowNotes = async (streamId: string) => {
    try {
      setLoadingShowNotes(true);
      setShowNotesError(null);
      const notes = await showNotesService.getShowNotes(streamId);
      setShowNotes(notes);
    } catch (err: any) {
      logger.info('[StreamDetail] Show notes unavailable', { error: err });
    } finally {
      setLoadingShowNotes(false);
    }
  };

  const handleGenerateShowNotes = async () => {
    if (!id || !stream) return;
    if (!requireAuth('shownotes', stream.name)) return;
    try {
      setGeneratingShowNotes(true);
      setShowNotesError(null);
      setShowNotes(null); // Clear stale state so error can render
      const notes = await showNotesService.generateShowNotes(id, {
        includeTranscript: true,
        includeChapters: true,
      });
      setShowNotes(notes);
      if (notes.status === 'processing') {
        // Will be polled by useEffect
      }
    } catch (err: any) {
      logger.error('[StreamDetail] Error generating show notes', err);
      setShowNotesError(err.message || 'Failed to generate show notes');
    } finally {
      setGeneratingShowNotes(false);
    }
  };

  const handleAnalyzeStream = async () => {
    if (!id || !stream) return;
    try {
      setProcessingDJSet(true);
      setDJSetError(null);
      let currentSet = djSet;
      if (!currentSet) {
        currentSet = await djSetService.createDJSet(id);
        setDJSet(currentSet);
      }
      const result = await djSetService.processDJSet(currentSet.setId, true);
      if (result.status === 'ready' && result.tracks) {
        setDJSetTracks(result.tracks);
        setDJSet(prev => prev ? { ...prev, status: 'ready', trackCount: result.tracks?.length || 0 } : null);
      } else if (result.status === 'processing') {
        setDJSet(prev => prev ? { ...prev, status: 'processing' } : null);
        setTimeout(() => loadDJSet(id), 5000);
      }
    } catch (err: any) {
      logger.error('[StreamDetail] Error processing DJ Set', err);
      setDJSetError(err.message || 'Failed to analyze stream');
    } finally {
      setProcessingDJSet(false);
    }
  };

  const handleTrackDigDeeper = useCallback(async (track: TrackSegment) => {
    if (!djSet) return;
    setSelectedTrack(track);
    setShowTrackVinylModal(true);
    setLoadingTrackVinyl(true);
    setTrackVinylMatches([]);
    try {
      const response = await djSetService.findTrackVinylMatches(djSet.setId, track.segmentId);
      setTrackVinylMatches(response.matches);
    } catch (err: any) {
      logger.error('[StreamDetail] Error loading track vinyl', err);
    } finally {
      setLoadingTrackVinyl(false);
    }
  }, [djSet]);

  const closeTrackVinylModal = useCallback(() => {
    setShowTrackVinylModal(false);
    setSelectedTrack(null);
    setTrackVinylMatches([]);
  }, []);

  const handlePlayStream = useCallback(() => {
    if (!stream || !audioUrl) return;
    playGlobalStream({
      streamId: stream.streamId,
      name: stream.name,
      description: stream.description,
      audioUrl: audioUrl,
      coverImage: stream.coverImage,
      uploaderUsername: stream.uploaderUsername,
      uploaderDisplayName: stream.uploaderDisplayName,
      waveformData: stream.waveformData,
    });
  }, [stream, audioUrl, playGlobalStream]);

  const handleAddToQueue = useCallback(() => {
    if (!stream || !audioUrl) return;
    if (!requireAuth('queue', stream.name)) return;
    addToQueue({
      streamId: stream.streamId,
      name: stream.name,
      description: stream.description,
      audioUrl: audioUrl,
      coverImage: stream.coverImage,
      uploaderUsername: stream.uploaderUsername,
      uploaderDisplayName: stream.uploaderDisplayName,
      waveformData: stream.waveformData,
    });
  }, [stream, audioUrl, requireAuth, addToQueue]);

  const handleFavoriteToggle = useCallback(async () => {
    if (!stream) return;
    if (!requireAuth('favorite', stream.name)) return;
    try {
      if (isFavorited) {
        await socialService.removeFavorite(stream.streamId);
        setIsFavorited(false);
        if (favoriteStats) setFavoriteStats({ ...favoriteStats, totalFavorites: favoriteStats.totalFavorites - 1 });
      } else {
        await socialService.addFavorite(stream.streamId);
        setIsFavorited(true);
        if (favoriteStats) setFavoriteStats({ ...favoriteStats, totalFavorites: favoriteStats.totalFavorites + 1 });
      }
    } catch (err: any) {
      logger.error('[StreamDetail] Error toggling favorite', err);
    }
  }, [stream, isFavorited, favoriteStats, requireAuth]);

  const handleShare = (platform: string) => {
    if (!stream) return;
    const url = window.location.href;
    const text = `Check out "${stream.name}" on Tempest Radio`;
    const shareUrls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      setShowShareMenu(false);
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
      setShowShareMenu(false);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !newComment.trim()) return;
    try {
      const input: { content: string; timestampMs?: number } = { content: newComment };
      // If commenting at timestamp and we're playing, include the timestamp
      if (commentAtTimestamp && isCurrentStream && currentTime > 0) {
        input.timestampMs = Math.floor(currentTime * 1000); // Convert seconds to milliseconds
      }
      await socialService.createComment(id, input);
      setNewComment('');
      setCommentAtTimestamp(false); // Reset after posting
      const commentsData = await socialService.getComments(id);
      setComments(commentsData);
    } catch (err: any) {
      logger.error('[StreamDetail] Error posting comment', err);
    }
  };

  // Handle click on a comment marker - seek to timestamp and highlight comment
  const handleMarkerClick = useCallback((marker: CommentMarker) => {
    // Seek to the timestamp if we're currently playing this stream
    if (isCurrentStream && marker.timestampMs) {
      seek(marker.timestampMs / 1000); // Convert ms to seconds
    } else if (stream && audioUrl) {
      // Start playing from this position
      playGlobalStream({
        streamId: stream.streamId,
        name: stream.name,
        audioUrl: audioUrl,
        coverImage: stream.coverImage,
        uploaderUsername: stream.uploaderUsername,
        uploaderDisplayName: stream.uploaderDisplayName,
        waveformData: stream.waveformData,
      });
      // Small delay to let player start, then seek
      setTimeout(() => seek(marker.timestampMs / 1000), 100);
    }

    // Highlight the first comment in this marker
    setHighlightedCommentId(marker.comments[0].commentId);

    // Scroll to comments section
    const commentsSection = document.querySelector('.v2-comments');
    if (commentsSection) {
      commentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Remove highlight after animation
    setTimeout(() => setHighlightedCommentId(null), 3000);
  }, [isCurrentStream, seek, stream, audioUrl, playGlobalStream]);

  const handleReplySubmit = async (e: React.FormEvent, parentId: string) => {
    e.preventDefault();
    if (!id || !replyText.trim()) return;
    try {
      await socialService.createComment(id, { content: replyText, parentId });
      setReplyText('');
      setReplyingTo(null);
      const commentsData = await socialService.getComments(id);
      setComments(commentsData);
    } catch (err: any) {
      logger.error('[StreamDetail] Error posting reply', err);
    }
  };

  const getReplies = (parentId: string): Comment[] => comments.filter(c => c.parentId === parentId);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimestampMs = (ms: number | null | undefined): string => {
    // TEM-1141: an assembled set's entries carry `startMs`, not `timestamp_ms`,
    // so this used to receive `undefined` and render the literal string
    // "NaN:NaN". Anything non-finite now renders as an em dash.
    if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return '—';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * TEM-1141 — resolve a tracklist entry's start offset across both producers.
   * DJ sets (Radio's AudD path) emit `timestamp_ms`; sets assembled and
   * published by Record emit `startMs` (serde camelCase on `AssembledTrack`).
   * Returns null when neither is usable, so callers can skip seeking rather
   * than call `seek(NaN)`.
   */
  const trackStartMs = (track: { timestamp_ms?: number; startMs?: number }): number | null => {
    const raw = typeof track.timestamp_ms === 'number' ? track.timestamp_ms : track.startMs;
    return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 ? raw : null;
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString();
  };

  // Loading
  if (loading) {
    return (
      <div className="stream-detail-v2">
        <div className="v2-loading">
          <div className="v2-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error || !stream) {
    return (
      <div className="stream-detail-v2">
        <div className="v2-error">
          <h2>Stream Not Found</h2>
          <p>{error || 'The requested stream could not be loaded.'}</p>
          <button onClick={() => navigate('/streams')} className="v2-btn-primary">Back to Streams</button>
        </div>
      </div>
    );
  }

  const isInQueue = queue.some(s => s.streamId === stream.streamId);

  return (
    <div className="stream-detail-v2">
      {/* Minimal Header */}
      <header className="v2-header">
        <button onClick={() => navigate('/streams')} className="v2-back">
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          <span>Streams</span>
        </button>
      </header>

      {/* Player Hero - Waveform Centric */}
      <section className="v2-player">
        {/* Left: Art + Info */}
        <div className="v2-player-info">
          <div className="v2-art">
            {stream.coverImage ? (
              <img src={stream.coverImage} alt={stream.name} />
            ) : (
              <div className="v2-art-placeholder">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" opacity="0.4">
                  <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                </svg>
              </div>
            )}
            {/* Play overlay on art */}
            <button onClick={handlePlayStream} className="v2-art-play">
              {isPlaying ? (
                <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              )}
            </button>
          </div>

          <div className="v2-meta">
            <h1 className="v2-title">{stream.name}</h1>
            <div className="v2-meta-row">
              {stream.uploaderUsername && (
                <Link to={`/users/${stream.uploaderUsername}`} className="v2-artist">
                  {stream.uploaderDisplayName || stream.uploaderUsername}
                </Link>
              )}
              <span className="v2-genre-tag">{normalizeGenre(stream.genre)}</span>
              {stream.metadata?.bpm_min != null && stream.metadata?.bpm_max != null && (
                <span className="v2-bpm-badge">
                  {Math.round(stream.metadata.bpm_min)}-{Math.round(stream.metadata.bpm_max)} BPM
                </span>
              )}
            </div>
            {stream.description && <p className="v2-desc">{stream.description}</p>}
          </div>
        </div>

        {/* Time display when playing */}
        {isCurrentStream && (
          <div className="v2-now-playing">
            <span className="v2-now-playing-label">Now Playing</span>
            <span className="v2-now-playing-time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
        )}

        {/* Actions Bar */}
        <div className="v2-actions">
          <div className="v2-actions-left">
            <button onClick={handlePlayStream} className="v2-play-btn">
              {isPlaying ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                  Pause
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                  Play
                </>
              )}
            </button>

            <button onClick={handleAddToQueue} className={`v2-action-btn ${isInQueue ? 'active' : ''}`} disabled={isInQueue}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
              </svg>
              {isInQueue ? 'Queued' : 'Queue'}
            </button>

            <button onClick={handleFavoriteToggle} className={`v2-action-btn ${isFavorited ? 'favorited' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={isFavorited ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
              {favoriteStats?.totalFavorites || 0}
            </button>

            <div className="v2-share-wrap">
              <button onClick={() => setShowShareMenu(!showShareMenu)} className="v2-action-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                Share
              </button>
              {showShareMenu && (
                <div className="v2-share-dropdown">
                  <button onClick={() => handleShare('twitter')}>Twitter</button>
                  <button onClick={() => handleShare('facebook')}>Facebook</button>
                  <button onClick={() => handleShare('copy')}>Copy Link</button>
                </div>
              )}
            </div>
          </div>

          <div className="v2-actions-right">
            <DownloadButton stream={stream} size="small" />
          </div>
        </div>

        {/* Stream Stats Bar */}
        <div className="v2-stats-bar">
          <div className="v2-stat-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            <div className="v2-stat-value">{playStats?.totalPlays?.toLocaleString() || 0}</div>
            <div className="v2-stat-label">plays</div>
          </div>
          <div className="v2-stat-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            </svg>
            <div className="v2-stat-value">{playStats?.uniqueUsers?.toLocaleString() || 0}</div>
            <div className="v2-stat-label">listeners</div>
          </div>
          {((isCurrentStream && duration > 0) || stream.metadata?.duration) && (
            <div className="v2-stat-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              <div className="v2-stat-value">{formatTime(isCurrentStream && duration > 0 ? duration : (stream.metadata?.duration || 0))}</div>
              <div className="v2-stat-label">duration</div>
            </div>
          )}
          <div className="v2-stat-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <div className="v2-stat-value">{stream.createdAt ? formatDate(stream.createdAt) : 'Unknown'}</div>
            <div className="v2-stat-label">uploaded</div>
          </div>
          <div className="v2-stat-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
            </svg>
            <div className="v2-stat-value">{stream.format?.toUpperCase() || 'MP3'}</div>
            <div className="v2-stat-label">{stream.bitrate || 128}kbps</div>
          </div>
        </div>

        {/* Waveform with Comment Markers */}
        <div className="v2-waveform-container">
          <WaveformViewer
            audioUrl={audioUrl}
            currentTime={isCurrentStream ? currentTime : 0}
            duration={isCurrentStream ? duration : 0}
            onSeek={(time) => {
              if (isCurrentStream) {
                seek(time);
              } else if (stream) {
                // Start playing first, then seek
                playGlobalStream({
                  streamId: stream.streamId,
                  name: stream.name,
                  audioUrl: audioUrl,
                  coverImage: stream.coverImage,
                  uploaderUsername: stream.uploaderUsername,
                  uploaderDisplayName: stream.uploaderDisplayName,
                  waveformData: stream.waveformData,
                });
                setTimeout(() => seek(time), 100);
              }
            }}
            height={80}
            waveformData={stream.waveformData}
          />
          {commentMarkers.length > 0 && duration > 0 && (
            <WaveformCommentMarkers
              markers={commentMarkers}
              duration={duration}
              onMarkerClick={handleMarkerClick}
              height={80}
            />
          )}
        </div>
      </section>

      {/* Track Analysis */}
      <section className="v2-section">
        <h2 className="v2-section-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
          </svg>
          Track Analysis
          {djSet?.status === 'ready' && djSetTracks.length > 0 && (
            <span className="v2-badge-count">{djSetTracks.length} tracks</span>
          )}
        </h2>

        {djSet?.status === 'ready' && djSetTracks.length > 0 ? (
          <div className="v2-tracks-list">
            <TrackTimeline
              audioUrl={audioUrl}
              currentTime={isCurrentStream ? currentTime : 0}
              duration={duration || 0}
              onSeek={isCurrentStream ? seek : () => {}}
              tracks={djSetTracks}
              onDigDeeper={handleTrackDigDeeper}
              height={60}
              waveformData={stream.waveformData}
              showTrackList={true}
              showWaveform={false}
            />
            {isOwner && (
              <button onClick={handleAnalyzeStream} className="v2-btn-secondary" style={{ marginTop: '12px' }}>
                Re-analyze Tracks
              </button>
            )}
          </div>
        ) : (djSet?.status === 'processing' && djSet.updatedAt && (Date.now() - new Date(djSet.updatedAt).getTime()) > 10 * 60 * 1000) ? (
          <div className="v2-error-inline">
            <p>Track analysis timed out. Please try again.</p>
            {isOwner && <button onClick={handleAnalyzeStream} className="v2-btn-secondary">Try Again</button>}
          </div>
        ) : djSet?.status === 'processing' ? (
          <div className="v2-loading-inline">
            <div className="v2-spinner-small" />
            <div>
              <p>Analyzing tracks...</p>
              <p className="v2-loading-hint">Identifying tracks using audio fingerprinting</p>
            </div>
          </div>
        ) : djSet?.status === 'failed' ? (
          <div className="v2-error-inline">
            <p>Track analysis failed.</p>
            {isOwner && <button onClick={handleAnalyzeStream} className="v2-btn-secondary">Try Again</button>}
          </div>
        ) : isOwner ? (
          <div className="v2-analyze-prompt">
            <p>Identify tracks in this stream using AI audio fingerprinting.</p>
            <button onClick={handleAnalyzeStream} className="v2-btn-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
              </svg>
              Analyze Tracks
            </button>
          </div>
        ) : (
          <p className="v2-empty-hint">No track analysis available for this stream.</p>
        )}
      </section>

      {/* Show Notes Section */}
      <section className="v2-section">
        <h2 className="v2-section-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
          Show Notes
          {showNotes?.status === 'ready' && showNotes.chapters && (
            <span className="v2-badge-count">{showNotes.chapters.length} chapters</span>
          )}
        </h2>

        {loadingShowNotes ? (
          <div className="v2-loading-inline"><div className="v2-spinner-small" /> Loading...</div>
        ) : (showNotes?.status === 'processing' && showNotes.updatedAt && (Date.now() - new Date(showNotes.updatedAt).getTime()) > 10 * 60 * 1000) ? (
          <div className="v2-error-inline">
            <p>Show notes generation timed out. Please try again.</p>
            {isOwner && <button onClick={handleGenerateShowNotes} className="v2-btn-secondary">Try Again</button>}
          </div>
        ) : showNotes?.status === 'processing' || generatingShowNotes ? (
          <div className="v2-loading-inline">
            <div className="v2-spinner-small" />
            <div>
              <p>Generating show notes...</p>
              <p className="v2-loading-hint">This may take a few minutes for longer streams</p>
            </div>
          </div>
        ) : showNotes?.status === 'ready' ? (
          <div className="v2-show-notes">
            {/* Summary */}
            {showNotes.summary && (
              <div className="v2-show-notes-summary">
                <h3>Summary</h3>
                <p>{showNotes.summary}</p>
              </div>
            )}

            {/* Key Topics */}
            {showNotes.keyTopics && showNotes.keyTopics.length > 0 && (
              <div className="v2-show-notes-topics">
                <h3>Key Topics</h3>
                <div className="v2-topics-list">
                  {showNotes.keyTopics.map((topic, i) => (
                    <span key={i} className="v2-topic-tag">{topic}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Mood & Tags */}
            <div className="v2-show-notes-meta">
              {showNotes.mood && (
                <div className="v2-meta-item">
                  <span className="v2-meta-label">Mood</span>
                  <span className="v2-meta-value v2-mood-tag">{showNotes.mood}</span>
                </div>
              )}
              {showNotes.tags && showNotes.tags.length > 0 && (
                <div className="v2-meta-item">
                  <span className="v2-meta-label">Tags</span>
                  <div className="v2-tags-list">
                    {showNotes.tags.map((tag, i) => (
                      <span key={i} className="v2-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Notable Quotes */}
            {showNotes.notableQuotes && showNotes.notableQuotes.length > 0 && (
              <div className="v2-show-notes-quotes">
                <h3>Notable Quotes</h3>
                <div className="v2-quotes-list">
                  {showNotes.notableQuotes.map((quote, i) => (
                    <blockquote key={i} className="v2-quote">{quote}</blockquote>
                  ))}
                </div>
              </div>
            )}

            {/* Chapters */}
            {showNotes.chapters && showNotes.chapters.length > 0 && (
              <div className="v2-show-notes-chapters">
                <h3>Chapters</h3>
                <div className="v2-chapters-list">
                  {showNotes.chapters.map((chapter, i) => (
                    <div
                      key={i}
                      className="v2-chapter"
                      onClick={() => isCurrentStream && seek(chapter.startTime)}
                      role={isCurrentStream ? 'button' : undefined}
                      tabIndex={isCurrentStream ? 0 : undefined}
                    >
                      <span className="v2-chapter-time">
                        {showNotesService.formatChapterTime(chapter.startTime)}
                      </span>
                      <div className="v2-chapter-info">
                        <span className="v2-chapter-title">{chapter.title}</span>
                        {chapter.description && (
                          <span className="v2-chapter-desc">{chapter.description}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transcript Toggle */}
            {showNotes.transcript && (
              <div className="v2-show-notes-transcript">
                <button
                  className="v2-transcript-toggle"
                  onClick={() => setShowTranscript(!showTranscript)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points={showTranscript ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}/>
                  </svg>
                  {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
                </button>
                {showTranscript && (
                  <div className="v2-transcript-content">
                    <pre>{showNotes.transcript}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : showNotesError ? (
          <div className="v2-error-inline">
            <p>{showNotesError}</p>
            {isOwner && <button onClick={handleGenerateShowNotes} className="v2-btn-secondary">Try Again</button>}
          </div>
        ) : isOwner ? (
          <div className="v2-analyze-prompt">
            <p>Generate AI-powered show notes with transcription, summary, and chapters.</p>
            <button onClick={handleGenerateShowNotes} className="v2-btn-primary" disabled={generatingShowNotes}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
              Generate Show Notes
            </button>
          </div>
        ) : (
          <p className="v2-empty-hint">No show notes available for this stream.</p>
        )}
      </section>

      {/* DJ Tracklist - from Tempest DJ metadata */}
      {stream.metadata?.tracklist && stream.metadata.tracklist.length > 0 && (
        <section className="v2-section">
          <h2 className="v2-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
            </svg>
            Tracklist
            <span className="v2-badge-count">{stream.metadata.tracklist.length} tracks</span>
          </h2>
          <div className="v2-tracklist-table-wrap">
            <table className="v2-tracklist-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Title</th>
                  <th>Artist</th>
                  <th>BPM</th>
                  <th>Key</th>
                </tr>
              </thead>
              <tbody>
                {stream.metadata.tracklist.map((track, i) => (
                  <tr
                    key={i}
                    className="v2-tracklist-row"
                    onClick={() => {
                      const startMs = trackStartMs(track);
                      if (isCurrentStream) {
                        if (startMs !== null) seek(startMs / 1000);
                      } else if (stream) {
                        playGlobalStream({
                          streamId: stream.streamId,
                          name: stream.name,
                          audioUrl: audioUrl,
                          coverImage: stream.coverImage,
                          uploaderUsername: stream.uploaderUsername,
                          uploaderDisplayName: stream.uploaderDisplayName,
                          waveformData: stream.waveformData,
                        });
                        if (startMs !== null) setTimeout(() => seek(startMs / 1000), 100);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <td className="v2-tracklist-time">{formatTimestampMs(trackStartMs(track))}</td>
                    <td className="v2-tracklist-title">{track.title}</td>
                    <td className="v2-tracklist-artist">{track.artist}</td>
                    <td className="v2-tracklist-bpm">{track.bpm ? Math.round(track.bpm) : '-'}</td>
                    <td className="v2-tracklist-key">{track.key || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Heritage / Lineage - carried from the Tempest DJ set upload (TEM-989).
          Read-only; renders nothing when the set carried no collection lineage. */}
      <LineageHeritage lineage={stream.metadata?.lineage} />

      {/* Social Engagement - The Pulse (only show when there's engagement data) */}
      {((playStats && (playStats.totalPlays > 0 || playStats.playedBy.length > 0)) ||
        (favoriteStats && (favoriteStats.totalFavorites > 0 || favoriteStats.favoritedBy.length > 0))) && (
        <section className="v2-section v2-engagement">
          <h2 className="v2-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
            </svg>
            The Pulse
          </h2>

          <div className="v2-engagement-grid">
            {/* Listeners */}
            <div className="v2-engagement-block">
              <div className="v2-engagement-header">
                <h3>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                  </svg>
                  Listeners
                  <span className="v2-engagement-count">({playStats?.uniqueUsers?.toLocaleString() || 0})</span>
                </h3>
                {playStats && playStats.uniqueUsers > 5 && (
                  <button className="v2-see-all-btn">See all</button>
                )}
              </div>
              <div className="v2-avatar-row">
                {playStats && playStats.playedBy.length > 0 ? (
                  <>
                    {playStats.playedBy.slice(0, 5).map((user) => (
                      <Link key={user.userId} to={`/users/${user.username}`} className="v2-big-avatar" title={user.username}>
                        <div className="v2-avatar-img">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        {user.playCount > 1 && (
                          <span className="v2-play-badge">{user.playCount}×</span>
                        )}
                      </Link>
                    ))}
                    {playStats.playedBy.length > 5 && (
                      <div className="v2-avatar-overflow">
                        +{playStats.playedBy.length - 5}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="v2-empty-avatars">
                    <span>Be the first to listen</span>
                  </div>
                )}
              </div>
            </div>

            {/* Favorited By */}
            <div className="v2-engagement-block">
              <div className="v2-engagement-header">
                <h3>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="v2-heart-icon">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  Favorited by
                  <span className="v2-engagement-count">({favoriteStats?.totalFavorites?.toLocaleString() || 0})</span>
                </h3>
                {favoriteStats && favoriteStats.totalFavorites > 5 && (
                  <button className="v2-see-all-btn">See all</button>
                )}
              </div>
              <div className="v2-avatar-row">
                {favoriteStats && favoriteStats.favoritedBy.length > 0 ? (
                  <>
                    {favoriteStats.favoritedBy.slice(0, 5).map((user) => (
                      <Link key={user.userId} to={`/users/${user.username}`} className="v2-big-avatar" title={user.username}>
                        <div className="v2-avatar-img v2-avatar-fav">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                      </Link>
                    ))}
                    {favoriteStats.favoritedBy.length > 5 && (
                      <div className="v2-avatar-overflow">
                        +{favoriteStats.favoritedBy.length - 5}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="v2-empty-avatars">
                    <span>No favorites yet</span>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity Feed */}
            <div className="v2-engagement-block v2-recent-activity">
              <div className="v2-engagement-header">
                <h3>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                  Recent Activity
                </h3>
              </div>
              <div className="v2-activity-feed">
                {playStats && playStats.playedBy.slice(0, 3).map((user, i) => (
                  <div key={`play-${user.userId}`} className="v2-feed-item">
                    <Link to={`/users/${user.username}`} className="v2-feed-avatar">
                      {user.username.charAt(0).toUpperCase()}
                    </Link>
                    <div className="v2-feed-content">
                      <Link to={`/users/${user.username}`} className="v2-feed-user">{user.username}</Link>
                      <span className="v2-feed-action">played this stream</span>
                    </div>
                    <span className="v2-feed-time">{i === 0 ? 'Just now' : i === 1 ? '2m ago' : '5m ago'}</span>
                  </div>
                ))}
                {favoriteStats && favoriteStats.favoritedBy.slice(0, 2).map((user, i) => (
                  <div key={`fav-${user.userId}`} className="v2-feed-item">
                    <Link to={`/users/${user.username}`} className="v2-feed-avatar v2-feed-avatar-fav">
                      {user.username.charAt(0).toUpperCase()}
                    </Link>
                    <div className="v2-feed-content">
                      <Link to={`/users/${user.username}`} className="v2-feed-user">{user.username}</Link>
                      <span className="v2-feed-action">favorited this stream</span>
                    </div>
                    <span className="v2-feed-time">{i === 0 ? '10m ago' : '1h ago'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Content Grid */}
      <div className="v2-grid">
        {/* Comments */}
        <section className="v2-section v2-comments">
          <h2 className="v2-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Comments
            <span className="v2-badge-count">{comments.length}</span>
          </h2>

          <form onSubmit={handleCommentSubmit} className="v2-comment-form">
            <textarea
              placeholder="Share your thoughts..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              rows={2}
            />
            <div className="v2-comment-form-actions">
              {/* Timestamp toggle - only show when stream is playing */}
              {isCurrentStream && currentTime > 0 && (
                <label className="v2-timestamp-toggle">
                  <input
                    type="checkbox"
                    checked={commentAtTimestamp}
                    onChange={(e) => setCommentAtTimestamp(e.target.checked)}
                  />
                  <span className="v2-timestamp-label">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    Comment at {formatTime(currentTime)}
                  </span>
                </label>
              )}
              <button type="submit" disabled={!newComment.trim()} className="v2-btn-primary">Post</button>
            </div>
          </form>

          {/* Comment filter tabs */}
          <div className="v2-comment-filters">
            <button
              className={`v2-filter-btn ${commentFilter === 'all' ? 'active' : ''}`}
              onClick={() => setCommentFilter('all')}
            >
              All ({comments.filter(c => !c.parentId).length})
            </button>
            <button
              className={`v2-filter-btn ${commentFilter === 'timestamped' ? 'active' : ''}`}
              onClick={() => setCommentFilter('timestamped')}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              Timed ({comments.filter(c => c.timestampMs !== undefined && !c.parentId).length})
            </button>
            <button
              className={`v2-filter-btn ${commentFilter === 'general' ? 'active' : ''}`}
              onClick={() => setCommentFilter('general')}
            >
              General ({comments.filter(c => c.timestampMs === undefined && !c.parentId).length})
            </button>
          </div>

          <div className="v2-comments-list">
            {loadingComments ? (
              <div className="v2-loading-inline"><div className="v2-spinner-small" /></div>
            ) : filteredComments.length === 0 ? (
              <p className="v2-empty">
                {commentFilter === 'all'
                  ? 'No comments yet. Be the first!'
                  : commentFilter === 'timestamped'
                  ? 'No timed comments yet. Play the stream and leave a comment at a specific moment!'
                  : 'No general comments yet.'}
              </p>
            ) : (
              filteredComments.map((comment) => {
                const replies = getReplies(comment.commentId);
                const isHighlighted = highlightedCommentId === comment.commentId;
                return (
                  <div
                    key={comment.commentId}
                    className={`v2-comment ${isHighlighted ? 'v2-comment-highlighted' : ''}`}
                    id={`comment-${comment.commentId}`}
                  >
                    <div className="v2-comment-header">
                      <span className="v2-comment-author">{comment.username}</span>
                      {comment.timestampMs !== undefined && (
                        <button
                          className="v2-comment-timestamp"
                          onClick={() => {
                            if (isCurrentStream) {
                              seek(comment.timestampMs! / 1000);
                            } else if (stream) {
                              playGlobalStream({
                                streamId: stream.streamId,
                                name: stream.name,
                                audioUrl: audioUrl,
                                coverImage: stream.coverImage,
                                uploaderUsername: stream.uploaderUsername,
                                uploaderDisplayName: stream.uploaderDisplayName,
                                waveformData: stream.waveformData,
                              });
                              setTimeout(() => seek(comment.timestampMs! / 1000), 100);
                            }
                          }}
                          title="Jump to this moment"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                          </svg>
                          {formatTime(comment.timestampMs / 1000)}
                        </button>
                      )}
                      <span className="v2-comment-date">{formatDate(comment.createdAt)}</span>
                    </div>
                    <p className="v2-comment-body">{comment.content}</p>
                    <button className="v2-comment-reply-btn" onClick={() => setReplyingTo(replyingTo === comment.commentId ? null : comment.commentId)}>
                      Reply
                    </button>

                    {replyingTo === comment.commentId && (
                      <form onSubmit={(e) => handleReplySubmit(e, comment.commentId)} className="v2-reply-form">
                        <input
                          type="text"
                          placeholder="Write a reply..."
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                        />
                        <button type="submit" disabled={!replyText.trim()}>Reply</button>
                      </form>
                    )}

                    {replies.length > 0 && (
                      <div className="v2-replies">
                        {replies.map((reply) => (
                          <div key={reply.commentId} className="v2-reply">
                            <span className="v2-comment-author">{reply.username}</span>
                            <span className="v2-comment-date">{formatDate(reply.createdAt)}</span>
                            <p>{reply.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Sidebar */}
        <aside className="v2-sidebar">
          {/* Similar Streams */}
          <div className="v2-sidebar-section">
            <h3 className="v2-sidebar-title">Similar Streams</h3>
            {loadingSimilar ? (
              <div className="v2-loading-inline"><div className="v2-spinner-small" /></div>
            ) : similarStreams.length > 0 ? (
              <div className="v2-similar-list">
                {similarStreams.map((similar) => (
                  <Link key={similar.id} to={`/streams/${similar.payload.stream_id || similar.id}`} className="v2-similar-item">
                    <div className="v2-similar-art">
                      {similar.payload.cover_image ? (
                        <img src={similar.payload.cover_image} alt="" />
                      ) : (
                        <div className="v2-similar-art-placeholder" />
                      )}
                    </div>
                    <div className="v2-similar-info">
                      <span className="v2-similar-name">{similar.payload.title || 'Untitled'}</span>
                      <span className="v2-similar-genre">{taggedGenre(similar.payload.genre) ?? ''}</span>
                    </div>
                    {similar.score && <span className="v2-similar-score">{Math.round(similar.score * 100)}%</span>}
                  </Link>
                ))}
              </div>
            ) : (
              <p className="v2-empty">No similar streams found</p>
            )}
          </div>

          {/* Dig Deeper - Vinyl */}
          {(vinylMatches.length > 0 || vinylMessage || loadingVinyl) && (
            <div className="v2-sidebar-section">
              <h3 className="v2-sidebar-title">
                Dig Deeper
                <span className="v2-sidebar-subtitle">from your vinyl collection</span>
              </h3>
              {loadingVinyl ? (
                <div className="v2-loading-inline"><div className="v2-spinner-small" /></div>
              ) : vinylMessage ? (
                <p className="v2-empty">{vinylMessage}</p>
              ) : (
                <div className="v2-vinyl-list">
                  {vinylMatches.map((vinyl) => (
                    <DigDeeperRow key={vinyl.id} matchId={vinyl.id}>
                      <div className="v2-vinyl-art">
                        {vinyl.coverImage ? <img src={vinyl.coverImage} alt="" /> : <div className="v2-vinyl-art-placeholder" />}
                      </div>
                      <div className="v2-vinyl-info">
                        <span className="v2-vinyl-name">{vinyl.title}</span>
                        {vinyl.artist && <span className="v2-vinyl-artist">{vinyl.artist}</span>}
                      </div>
                      <span className="v2-vinyl-score">{Math.round(vinyl.score * 100)}%</span>
                    </DigDeeperRow>
                  ))}
                </div>
              )}
            </div>
          )}

        </aside>
      </div>

      {/* Track Vinyl Modal */}
      {showTrackVinylModal && selectedTrack && (
        <div className="v2-modal-overlay" onClick={closeTrackVinylModal}>
          <div className="v2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="v2-modal-header">
              <h3>Vinyl Matches for "{selectedTrack.title || 'Unknown Track'}"</h3>
              <button onClick={closeTrackVinylModal} className="v2-modal-close">×</button>
            </div>
            <div className="v2-modal-content">
              {loadingTrackVinyl ? (
                <div className="v2-loading-inline"><div className="v2-spinner-small" /> Searching...</div>
              ) : trackVinylMatches.length > 0 ? (
                <div className="v2-vinyl-list">
                  {trackVinylMatches.map((match) => {
                    // TEM-635: surface the deep per-track credits + pressing the
                    // matched collection album already carries (from the shared
                    // Qdrant `media` payload). All defensive — render a line only
                    // when that field is present on the match.
                    const pressing = buildPressingSummary(match);
                    return (
                    <DigDeeperRow key={match.id} matchId={match.id}>
                      <div className="v2-vinyl-art">
                        {match.coverImage ? <img src={match.coverImage} alt="" /> : <div className="v2-vinyl-art-placeholder" />}
                      </div>
                      <div className="v2-vinyl-info">
                        <span className="v2-vinyl-name">{match.title}</span>
                        {match.artist && <span className="v2-vinyl-artist">{match.artist}</span>}
                        {match.producer && <span className="v2-vinyl-credit">Producer: {match.producer}</span>}
                        {match.engineer && <span className="v2-vinyl-credit">Engineer: {match.engineer}</span>}
                        {match.masteredBy && <span className="v2-vinyl-credit">Mastered: {match.masteredBy}</span>}
                        {pressing && <span className="v2-vinyl-pressing">{pressing}</span>}
                      </div>
                      <span className="v2-vinyl-score">{Math.round(match.score * 100)}%</span>
                    </DigDeeperRow>
                    );
                  })}
                </div>
              ) : (
                <p className="v2-empty">No vinyl matches found for this track.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamDetail;
